import { useQuery } from '@tanstack/react-query';

import { useCallback } from 'react';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { queries } from '@config/server/queryKeys';

import { getInventory } from '../api/useGetInventory';
import { usePublishInventoryMutation } from '../api/usePublishInventory';

/**
 * Custom hook for bulk publishing inventory items
 * Uses server-side bulk publish endpoint for efficiency
 * Supports publishing specific items via inventoryIds or all draft items
 *
 * Uses React Query for the draft count.
 * The count is automatically refetched when inventory is invalidated (e.g., after publishing).
 */
export const usePublishAllInventory = () => {
	const publishMutation = usePublishInventoryMutation();
	const currentServicesProviderId = useCurrentServicesProviderId();

	// Use React Query to fetch draft count with caching and deduplication
	const draftCountQuery = useQuery({
		queryKey: queries.inventory.list(currentServicesProviderId ?? '', {
			limit: 1,
			filters: { publishStatus: ['draft'] },
		}).queryKey,
		queryFn: () =>
			getInventory(currentServicesProviderId!, {
				limit: 1, // Only fetch 1 item to minimize data transfer
				filters: {
					publishStatus: ['draft'],
				},
			}),
		enabled: !!currentServicesProviderId,
		select: (data) => data.count,
	});

	/**
	 * Publish inventory items
	 * When inventoryIds is provided and non-empty, only those items will be published
	 * When inventoryIds is empty or not provided, all draft items will be published
	 */
	const handlePublish = useCallback(
		async (inventoryIds?: string[]) => {
			if (!currentServicesProviderId) {
				console.error('No services provider ID available');
				return;
			}

			try {
				// Call the server-side bulk publish endpoint
				// If inventoryIds is provided and non-empty, pass it; otherwise publish all
				await publishMutation.mutateAsync({
					servicesProviderId: currentServicesProviderId,
					inventoryIds:
						inventoryIds && inventoryIds.length > 0 ? inventoryIds : undefined,
				});
			} catch (error) {
				console.error('Error publishing inventory items:', error);
			}
		},
		[currentServicesProviderId, publishMutation],
	);

	return {
		handlePublish,
		isPublishing: publishMutation.isPending,
		publishableCount: draftCountQuery.data ?? null,
		isFetchingPublishableCount: draftCountQuery.isPending,
	};
};
